import React, { useEffect, useState, useCallback } from "react";
import { CustomerService } from "../../services/CustomerService";
import { CountGroupedByDateDto } from "../../services/Dtos";
import { TopStatsDto } from "../../services/Dtos/TopStatsDto";
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface StatsDisplayCustomerApiKeyProps {
  apiKeyId: number;
  onTileSelect: (tile: string | null) => void;
}

const StatsDisplayCustomerApiKey: React.FC<StatsDisplayCustomerApiKeyProps> = ({ apiKeyId, onTileSelect }) => {
  const [stats, setStats] = useState<TopStatsDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTile, setSelectedTile] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<Date>(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  });
  const [endDate, setEndDate] = useState<Date>(() => {
    const date = new Date();
    date.setHours(date.getHours() + 1, 0, 0, 0);
    return date;
  });
  const [groupByScale, setGroupByScale] = useState<'minute' | 'hour' | 'day'>('hour');
  const [graphData, setGraphData] = useState<CountGroupedByDateDto[]>([]);

  const loadStats = useCallback(async () => {
    try {
      setRefreshing(true);
      const response = await CustomerService.requestAnalytics.topStats.byApiKeyId(apiKeyId);
      setStats(response.data);
      setError(null);
    } catch (err) {
      setError("Failed to load stats");
      console.error("Error loading stats:", err);
    } finally {
      setIsLoading(false);
      setRefreshing(false);
    }
  }, [apiKeyId]);

  const loadGraphData = useCallback(async () => {
    if (!selectedTile) return;

    try {
      setIsLoading(true);
      const response = await CustomerService.requestAnalytics.stats.grouped(
        startDate.toISOString(),
        endDate.toISOString(),
        groupByScale,
        apiKeyId,
        undefined,
        selectedTile
      );
      setGraphData(response.data);
    } catch (err) {
      setError("Failed to load graph data");
      console.error("Error loading graph data:", err);
    } finally {
      setIsLoading(false);
    }
  }, [selectedTile, startDate, endDate, groupByScale, apiKeyId]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (selectedTile) {
      loadGraphData();
    }
  }, [selectedTile, loadGraphData]);

  const chartData = {
    labels: graphData.map(item => {
      const date = new Date(item.timestamp);
      switch (groupByScale) {
        case 'minute':
          return `${date.getHours()}:${date.getMinutes().toString().padStart(2, '0')}`;
        case 'hour':
          return `${date.getHours()}:00`;
        case 'day':
          return date.toLocaleDateString();
        default:
          return date.toLocaleString();
      }
    }),
    datasets: [
      {
        label: 'Total',
        data: graphData.map(item => item.count),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.1,
        fill: true
      },
      {
        label: 'Better Trucks',
        data: graphData.map(item => item.btCount),
        borderColor: 'rgb(54, 162, 235)',
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        tension: 0.1,
        fill: true
      },
      {
        label: 'Nominatim',
        data: graphData.map(item => item.nominatimCount),
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        tension: 0.1,
        fill: true
      },
      {
        label: 'Google',
        data: graphData.map(item => item.googleCount),
        borderColor: 'rgb(255, 159, 64)',
        backgroundColor: 'rgba(255, 159, 64, 0.2)',
        tension: 0.1,
        fill: true
      },
      {
        label: 'Radar',
        data: graphData.map(item => item.radarCount),
        borderColor: 'rgb(153, 102, 255)',
        backgroundColor: 'rgba(153, 102, 255, 0.2)',
        tension: 0.1,
        fill: true
      },
      {
        label: 'Google and Radar',
        data: graphData.map(item => item.googleRadarCount),
        borderColor: 'rgba(0, 125, 52, 1)',
        backgroundColor: 'rgba(0, 125, 52, 0.8)',
        tension: 0.1,
        fill: true
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: `Requests per ${groupByScale.charAt(0).toUpperCase() + groupByScale.slice(1)}`
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      }
    }
  };

  const handleTileSelect = (tile: string | null) => {
    setSelectedTile(tile);
    onTileSelect(tile);
  };

  if (isLoading && !stats) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger m-4" role="alert">
        {error}
      </div>
    );
  }

  return (
    <div className="container-fluid px-4">
      <div className="d-flex justify-content-end mb-4">
        <button 
          className="btn btn-outline-primary rounded-circle p-2 d-flex align-items-center justify-content-center" 
          onClick={loadStats}
          disabled={refreshing}
          style={{ width: '40px', height: '40px' }}
        >
          <i className={`bi bi-arrow-repeat ${refreshing ? "spin" : ""}`} style={{ fontSize: '1.2rem' }}></i>
        </button>
      </div>

      <div className="row g-4 mb-5">
        {/* Address Usage Card */}
        <div className="col-12 col-md-6 col-xxl-4">
          <div 
            className={`card stat-card h-100 border-0 rounded-4 shadow-sm transition-all ${selectedTile === 'validate' ? 'border-primary' : ''}`}
            style={{ cursor: 'pointer' }}
            onClick={() => handleTileSelect('validate')}
          >
            <div className="card-body p-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="text-uppercase fw-bold text-secondary mb-0">
                  <i className="bi bi-geo-alt me-2"></i>
                  Address Usage
                </h6>
              </div>
              <div className="d-flex align-items-center flex-wrap">
                <h2 className="stat-card-value fw-bold mb-0 text-black">
                  {stats?.totalAddressValidateRequestsCount.toLocaleString() || 0}
                </h2>
                <div className="ms-3 d-flex flex-column">
                  <span className="text-muted small">Total Requests</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Geocode Usage Card */}
        <div className="col-12 col-md-6 col-xxl-4">
          <div 
            className={`card stat-card h-100 border-0 rounded-4 shadow-sm transition-all ${selectedTile === 'geocode' ? 'border-primary' : ''}`}
            style={{ cursor: 'pointer' }}
            onClick={() => handleTileSelect('geocode')}
          >
            <div className="card-body p-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="text-uppercase fw-bold text-secondary mb-0">
                  <i className="bi bi-pin-map me-2"></i>
                  Geocode Usage
                </h6>
              </div>
              <div className="d-flex align-items-center flex-wrap">
                <h2 className="stat-card-value fw-bold mb-0 text-black">
                  {stats?.totalGeocodeRequestsCount.toLocaleString() || 0}
                </h2>
                <div className="ms-3 d-flex flex-column">
                  <span className="text-muted small">Total Requests</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Geocode Reverse Usage Card */}
        <div className="col-12 col-md-6 col-xxl-4">
          <div 
            className={`card stat-card h-100 border-0 rounded-4 shadow-sm transition-all ${selectedTile === 'geocode-reverse' ? 'border-primary' : ''}`}
            style={{ cursor: 'pointer' }}
            onClick={() => handleTileSelect('geocode-reverse')}
          >
            <div className="card-body p-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="text-uppercase fw-bold text-secondary mb-0">
                  <i className="bi bi-pin-map me-2"></i>
                  Geocode Reverse Usage
                </h6>
              </div>
              <div className="d-flex align-items-center flex-wrap">
                <h2 className="stat-card-value fw-bold mb-0 text-black">
                  {stats?.totalGeocodeReverseRequestCount.toLocaleString() || 0}
                </h2>
                <div className="ms-3 d-flex flex-column">
                  <span className="text-muted small">Total Requests</span>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      {selectedTile && (
        <div className="card border-0 rounded-4 shadow-sm mb-4">
          <div className="card-body p-4">
            <div className="d-flex gap-3 align-items-end justify-content-start mb-4">
              <div style={{ width: '200px' }}>
                <label className="form-label">From Date</label>
                <DatePicker
                  selected={startDate}
                  onChange={(date: Date | null) => date && setStartDate(date)}
                  className="form-control"
                  showTimeSelect
                  dateFormat="MMMM d, yyyy h:mm aa"
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  timeCaption="Time"
                />
              </div>
              <div style={{ width: '200px' }}>
                <label className="form-label">To Date</label>
                <DatePicker
                  selected={endDate}
                  onChange={(date: Date | null) => date && setEndDate(date)}
                  className="form-control"
                  showTimeSelect
                  dateFormat="MMMM d, yyyy h:mm aa"
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  timeCaption="Time"
                />
              </div>
              <div style={{ width: '150px' }}>
                <label className="form-label">Group By</label>
                <select 
                  className="form-select"
                  value={groupByScale}
                  onChange={(e) => setGroupByScale(e.target.value as 'minute' | 'hour' | 'day')}
                >
                  <option value="minute">Minute</option>
                  <option value="hour">Hour</option>
                  <option value="day">Day</option>
                  <option value="week">Week</option>
                  <option value="month">Month</option>
                  <option value="year">Year</option>
                </select>
              </div>
              <div className="ms-auto">
                <div className="text-end">
                  <div className="d-flex flex-column gap-2">
                    <div className="d-flex align-items-center justify-content-end">
                      <span className="me-2" style={{ width: '12px', height: '12px', backgroundColor: 'rgb(75, 192, 192)', display: 'inline-block' }}></span>
                      <span className="text-muted">Total:</span>
                      <span className="ms-2 fw-bold">{graphData.reduce((sum, item) => sum + item.count, 0).toLocaleString()}</span>
                    </div>
                    <div className="d-flex align-items-center justify-content-end">
                      <span className="me-2" style={{ width: '12px', height: '12px', backgroundColor: 'rgb(54, 162, 235)', display: 'inline-block' }}></span>
                      <span className="text-muted">Better Trucks:</span>
                      <span className="ms-2 fw-bold">{graphData.reduce((sum, item) => sum + item.btCount, 0).toLocaleString()}</span>
                    </div>
                    <div className="d-flex align-items-center justify-content-end">
                      <span className="me-2" style={{ width: '12px', height: '12px', backgroundColor: 'rgb(255, 99, 132)', display: 'inline-block' }}></span>
                      <span className="text-muted">Nominatim:</span>
                      <span className="ms-2 fw-bold">{graphData.reduce((sum, item) => sum + item.nominatimCount, 0).toLocaleString()}</span>
                    </div>
                    <div className="d-flex align-items-center justify-content-end">
                      <span className="me-2" style={{ width: '12px', height: '12px', backgroundColor: 'rgb(255, 159, 64)', display: 'inline-block' }}></span>
                      <span className="text-muted">Google:</span>
                      <span className="ms-2 fw-bold">{graphData.reduce((sum, item) => sum + item.googleCount, 0).toLocaleString()}</span>
                    </div>
                    <div className="d-flex align-items-center justify-content-end">
                      <span className="me-2" style={{ width: '12px', height: '12px', backgroundColor: 'rgb(153, 102, 255)', display: 'inline-block' }}></span>
                      <span className="text-muted">Radar:</span>
                      <span className="ms-2 fw-bold">{graphData.reduce((sum, item) => sum + item.radarCount, 0).toLocaleString()}</span>
                    </div>
                    <div className="d-flex align-items-center justify-content-end">
                      <span className="me-2" style={{ width: '12px', height: '12px', backgroundColor: 'rgba(0, 125, 52, 0.8)', display: 'inline-block' }}></span>
                      <span className="text-muted">Google and Radar:</span>
                      <span className="ms-2 fw-bold">{graphData.reduce((sum, item) => sum + item.googleRadarCount, 0).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ height: '400px' }}>
              {isLoading ? (
                <div className="d-flex justify-content-center align-items-center h-100">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <Line data={chartData} options={chartOptions} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Add custom CSS
const style = document.createElement("style");
style.innerHTML = `
  .transition-all {
    transition: all 0.3s ease;
  }
  .number-value {
    display: inline-block;
    transition: all 0.5s ease-out;
  }
  .value-updated {
    animation: pulse-highlight 1.5s ease-out;
  }
  @keyframes pulse-highlight {
    0% {
      transform: scale(1);
      text-shadow: 0 0 0 rgba(0,0,0,0);
    }
    20% {
      transform: scale(1.1);
      text-shadow: 0 0 10px rgba(0,123,255,0.5);
    }
    100% {
      transform: scale(1);
      text-shadow: 0 0 0 rgba(0,0,0,0);
    }
  }
  .spin {
    animation: spin 1s linear infinite;
  }
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
document.head.appendChild(style);

export default StatsDisplayCustomerApiKey; 